import React, { createContext, useState, useCallback, ReactNode } from 'react';
import endpoints from '../utils/commonVariables/endpoints';
import api from '../services/api';
import {
    formatMostBookedHotelsData,
    getPeriodByKey,
} from '../utils/dashboard/dashbordUtils';
import { useNotification } from './NotificationsContext';
import useTranslate from '../customHooks/useTranslate';

export enum PeriodKey {
    CURRENT = 'current',
    LAST_MONTH = 'last_month',
    LAST_3_MONTHS = 'last_3_months',
    LAST_6_MONTHS = 'last_6_months',
    LAST_12_MONTHS = 'last_12_months',
}

export interface HistoryBookingsData {
    DateStats: string;
    CommitedCount: number;
    CommitedNights: number;
    CommitedTotal: number; //decimal
    CancelledCount: number;
    CancelledNights: number;
    CancelledTotal: number; //decimal
}

export interface HotelsBookedData {
    BookingCount: number;
    IdHotel: number;
    HotelName: string;
}

interface ChartState {
    isLoading: boolean;
    isDataLoaded: boolean;
    period: {
        start: string;
        end: string;
        key: PeriodKey;
    };
}

export interface HistoryBookingsState extends ChartState {
    data: HistoryBookingsData[];
}

interface MostBookedHotelsState extends ChartState {
    data: HotelsBookedData[];
}

interface DashboardContextType {
    bookingsHistory: HistoryBookingsState;
    hotelsBooked: MostBookedHotelsState;
    getChartsInitialData: () => void;
    changeBookingsHistoryPeriod: (newPeriod: PeriodKey) => void;
    changeHotelsBookedPeriod: (newPeriod: PeriodKey) => void;
}

export const ContextDashboard = createContext<DashboardContextType | undefined>(
    undefined
);

const DEFAULT_PERIOD_KEY = PeriodKey.LAST_MONTH;
const initialPeriod = getPeriodByKey(DEFAULT_PERIOD_KEY);

export const ProviderDashboard = ({ children }: { children: ReactNode }) => {
    const { setErrorMessage } = useNotification();
    const translate = useTranslate();

    const [bookingsHistory, setBookingsHistory] =
        useState<HistoryBookingsState>({
            isLoading: false,
            isDataLoaded: false,
            data: [],
            period: {
                start: initialPeriod.start,
                end: initialPeriod.end,
                key: DEFAULT_PERIOD_KEY,
            },
        });

    const [hotelsBooked, setHotelsBooked] = useState<MostBookedHotelsState>({
        isLoading: false,
        isDataLoaded: false,
        data: [],
        period: {
            start: initialPeriod.start,
            end: initialPeriod.end,
            key: DEFAULT_PERIOD_KEY,
        },
    });

    const getHistoryBookingsData = async (
        start: string = bookingsHistory.period.start,
        end: string = bookingsHistory.period.end
    ) => {
        const endpoint = endpoints.bookingStats(start, end);
        try {
            const { data } = await api.get(endpoint);

            setBookingsHistory(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: data,
            }));
        } catch (error) {
            console.log(error);
            setErrorMessage(translate('something_went_wrong'));
            setBookingsHistory(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: [],
            }));
        }
    };

    const getHotelsBooked = async (
        start: string = hotelsBooked.period.start,
        end: string = hotelsBooked.period.end
    ) => {
        const endpoint = endpoints.mostBookedHotels(start, end);
        try {
            const { data } = await api.get(endpoint);

            const formatedData = formatMostBookedHotelsData(data);
            setHotelsBooked(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: formatedData,
            }));
        } catch (error) {
            setErrorMessage(translate('something_went_wrong'));
            setHotelsBooked(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: [],
            }));
        }
    };

    const getChartsInitialData = useCallback(async () => {
        setBookingsHistory(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
        }));
        setHotelsBooked(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
        }));

        getHotelsBooked();
        getHistoryBookingsData();
    }, []);

    const changeBookingsHistoryPeriod = async (newPeriod: PeriodKey) => {
        const newPeriodData = getPeriodByKey(newPeriod);
        setBookingsHistory(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
            period: {
                ...prev.period,
                start: newPeriodData.start,
                end: newPeriodData.end,
                key: newPeriod,
            },
        }));
        await getHistoryBookingsData(newPeriodData.start, newPeriodData.end);
    };

    const changeHotelsBookedPeriod = async (newPeriod: PeriodKey) => {
        const newPeriodData = getPeriodByKey(newPeriod);
        setHotelsBooked(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
            period: {
                ...prev.period,
                start: newPeriodData.start,
                end: newPeriodData.end,
                key: newPeriod,
            },
        }));
        await getHotelsBooked(newPeriodData.start, newPeriodData.end);
    };

    return (
        <ContextDashboard.Provider
            value={{
                bookingsHistory,
                hotelsBooked,
                getChartsInitialData,
                changeBookingsHistoryPeriod,
                changeHotelsBookedPeriod,
            }}
        >
            {children}
        </ContextDashboard.Provider>
    );
};

export const useDashboard = () => {
    const context = React.useContext(ContextDashboard);
    if (context === undefined) {
        throw new Error('useDashboard must be used within a ProviderDashboard');
    }
    return context;
};

export const useHotelsBooked = () => {
    const context = React.useContext(ContextDashboard);
    if (context === undefined) {
        throw new Error(
            'useHotelsBooked must be used within a ProviderDashboard'
        );
    }
    return context.hotelsBooked;
};

export const useHistoryBookings = () => {
    const context = React.useContext(ContextDashboard);
    if (context === undefined) {
        throw new Error(
            'useHistoryBookings must be used within a ProviderDashboard'
        );
    }
    return context.bookingsHistory;
};
